import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import type { AttemptResult as AttemptResultDto } from "@/lib/practice-api";

/**
 * apps/web :: components/practice :: AttemptResult
 *
 * Post-submit result view. Shows:
 *   - correct/incorrect badge
 *   - the correct choice (spelled out — critical for "incorrect" state
 *     because the ChoiceList above is frozen + the user needs to see
 *     what they SHOULD have picked)
 *   - explanation (always shown — PR #111 guarantees a non-null string)
 *   - optional handbook citation
 *   - Next button that auto-focuses so Enter-to-advance works
 *
 * Accessibility: we announce the outcome in an `aria-live="polite"`
 * region (per the ChoiceList-is-now-read-only transition), and we move
 * focus to the Next button on mount so keyboard-only users have a clean
 * advance path without re-tabbing through the frozen choices.
 */
export interface AttemptResultProps {
  result: AttemptResultDto;
  choices: ReadonlyArray<{ id: string; text: string }>;
  chosenChoiceId: string;
  onNext: () => void;
}

export default function AttemptResult({
  result,
  choices,
  onNext,
}: AttemptResultProps) {
  const { t } = useTranslation();
  const nextBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    nextBtnRef.current?.focus();
  }, []);

  const correctChoice = choices.find((c) => c.id === result.correct_choice_id);
  const badgeLabel = result.correct
    ? t("practice.result.correct")
    : t("practice.result.incorrect");
  const badgeClass = result.correct
    ? "bg-success/10 text-success border border-success/40"
    : "bg-danger/10 text-danger border border-danger/40";

  return (
    <section
      data-testid="practice-result"
      aria-labelledby="practice-result-heading"
      className="mt-md rounded-lg border border-brand-primary/10 bg-white p-md shadow-sm sm:p-lg"
    >
      <div
        data-testid="practice-result-live"
        aria-live="polite"
        role="status"
        className="sr-only"
      >
        {badgeLabel}
      </div>
      <div className="flex items-center justify-between gap-sm">
        <h2
          id="practice-result-heading"
          data-testid="practice-result-badge"
          className={`inline-flex items-center rounded-full px-sm py-xs text-sm font-semibold uppercase tracking-wide ${badgeClass}`}
        >
          {badgeLabel}
        </h2>
      </div>

      {!result.correct && correctChoice && (
        <p
          data-testid="practice-result-correct-choice"
          className="mt-md text-sm text-neutral-800"
        >
          <span className="font-semibold">
            {t("practice.result.correctAnswerPrefix")}:
          </span>{" "}
          {correctChoice.text}
        </p>
      )}

      <p
        data-testid="practice-result-explanation"
        className="mt-md text-base text-neutral-900"
      >
        {result.explanation}
      </p>

      {result.handbook_section && (
        <p
          data-testid="practice-result-handbook"
          className="mt-sm text-xs text-neutral-600"
        >
          {t("practice.result.handbookPrefix")}: {result.handbook_section}
        </p>
      )}

      <div className="mt-lg">
        <button
          ref={nextBtnRef}
          type="button"
          data-testid="practice-result-next"
          onClick={onNext}
          className="inline-flex items-center rounded-md border border-brand-primary bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("practice.result.next")}
        </button>
      </div>
    </section>
  );
}
